import type { SessionResponse, Unternehmen } from "@kundeninfo/api-client";

export type Benutzer = NonNullable<SessionResponse["user"]>;

export type Sitzung = {
  user: Benutzer;
  companies: Unternehmen[];
};

/**
 * Ob dieser Benutzer eine fremde Bearbeitungssperre übernehmen darf: LEAD und ADMIN
 * (Leitungsfunktionen), EDITOR nicht. Entscheidet nur, ob die Oberfläche die Übernahme
 * ANBIETET — fail closed liegt beim Server, der Unberechtigte mit 403 abweist.
 */
export function darfSperreUebernehmen(user: Pick<Benutzer, "role">): boolean {
  return user.role === "LEAD" || user.role === "ADMIN";
}

/**
 * Das Unternehmen, dem der Benutzer zugeordnet ist — für Logo und Farbakzent im Kopf.
 * Ohne Zuordnung (homeCompanyId fehlt oder verweist auf kein sichtbares Unternehmen)
 * gibt es kein Logo; der Kopf zeigt dann nur den Schriftzug "Kundeninfo".
 */
export function homeCompany(sitzung: Sitzung): Unternehmen | null {
  const id = sitzung.user.homeCompanyId;
  if (id === undefined || id === null) return null;
  return sitzung.companies.find((c) => c.id === id) ?? null;
}

/**
 * Das ARBEITSUNTERNEHMEN (ME-Etappe 1, Aufgabe 6): das Unternehmen, unter dem der
 * Benutzer gerade arbeitet — es bestimmt Farbe (`data-unternehmen`) und Wortmarke und
 * wird im Wechsler der Kopfleiste umgestellt (`user.workCompanyId`).
 *
 * Reihenfolge der Rückfälle: die eigene Wahl, sonst das Heimatunternehmen, sonst ME (die
 * Vorgabe der Spec), sonst nichts. Eine Wahl, die auf kein SICHTBARES Unternehmen zeigt
 * (stillgelegt, Sichtbarkeit entzogen), zählt wie keine — die Sitzung liefert dann ohnehin
 * `null`, aber auch ein veralteter Wert soll nicht ins Leere greifen.
 */
export function arbeitsunternehmen(sitzung: Sitzung): Unternehmen | null {
  const gewaehlt = sitzung.user.workCompanyId;
  if (gewaehlt !== undefined && gewaehlt !== null) {
    const treffer = sitzung.companies.find((c) => c.id === gewaehlt);
    if (treffer !== undefined) return treffer;
  }
  return homeCompany(sitzung)
    ?? sitzung.companies.find((c) => c.shortCode.toUpperCase() === "ME")
    ?? null;
}
